import { apiService } from './api';

/** Mirrors the backend CouponDiscountType enum (serialised as an int). */
export enum CouponDiscountType {
  Percentage = 0,
  FlatAmount = 1,
}

export interface Coupon {
  id: number;
  code: string;
  description?: string;
  discountType: CouponDiscountType;
  discountValue: number;
  minOrderAmount?: number;
  maxDiscountAmount?: number;
  validFrom: string;
  validTo: string;
  usageLimit?: number;
  usageCount: number;
  isActive: boolean;
}

export interface CreateCouponDTO {
  code: string;
  description?: string;
  discountType: CouponDiscountType;
  discountValue: number;
  minOrderAmount?: number;
  maxDiscountAmount?: number;
  validFrom: string;
  validTo: string;
  usageLimit?: number;
  isActive: boolean;
}

export type UpdateCouponDTO = CreateCouponDTO;

export interface ValidateCouponResult {
  isValid: boolean;
  message: string;
  couponId?: number;
  discountAmount: number;
}

/**
 * The backend wraps every payload in ApiResponse<T>, so each call unwraps exactly once
 * via `response.data.data`. List endpoints carry their paging metadata as a sibling
 * `pagination` field on the same envelope rather than nested inside `data`.
 */
const couponService = {
  getAll: async (pageNumber = 1, pageSize = 100) => {
    const response = await apiService.get<Coupon[]>('/coupons', { pageNumber, pageSize });
    return {
      items: response.data.data ?? [],
      pagination: response.data.pagination,
    };
  },

  getById: async (id: number) => {
    const response = await apiService.get<Coupon>(`/coupons/${id}`);
    return response.data.data;
  },

  create: async (dto: CreateCouponDTO) => {
    const response = await apiService.post<Coupon>('/coupons', dto);
    return response.data.data;
  },

  update: async (id: number, dto: UpdateCouponDTO) => {
    const response = await apiService.put<Coupon>(`/coupons/${id}`, dto);
    return response.data.data;
  },

  delete: (id: number) => apiService.delete(`/coupons/${id}`),

  /**
   * Read-only check used by the POS/invoice screen before the invoice is saved —
   * returns the discount the code would apply without consuming a usage slot.
   */
  validate: async (code: string, amount: number) => {
    const response = await apiService.get<ValidateCouponResult>('/coupons/validate', { code, amount });
    return response.data.data;
  },
};

export default couponService;
